<template>
  <modal
    v-if="show"
    height="850px"
    width="1020px"
    bodyPadding="0"
    class="details-dialog"
    :title="modalTitle"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${$i18n.locale}/campaign-creation-details`"
  >
    <template v-if="step === 1">
      <div class="dialog-wrapper">
        <create-campaign-dialog-panel-one
          :form="form"
          :defaultForm="defaultForm()"
          :errors-dict="errorsDict"
          :edit="edit"
          :defaultWorkspace="defaultWorkspace"
          :workspacesList="workspacesList"
          :allWorkspaceIds="allWorkspaceIds"
          :campaignInitialState="campaignInitialState"
          :offerOptions="offerOptions"
          @change-tracking-domain="changeTrackingDomain"
          @change-workspace="onChangeWorkspace"
          @change-country="onChangeCountry"
          :closeModal="close"
        ></create-campaign-dialog-panel-one>

        <div
          ref="scrollPanelDestinationRef"
          class="details campaign-details create-campaign-dialog-panel-destination"
        >
          <div class="subtitle">
            2. {{ t('Destination') }}
          </div>
          <div class="p-16 campaign-details__wrap campaign-details__wrap--right-panel">
            <div
              class="d-flex"
              v-bind="getIntroParams(4)"
            >
              <ui-radio-group
                v-model="form.destinationType"
                class="w50 mb-16"
                @change="destinationTypeChange"
                :label="t('Destination')"
              >
                <ui-radio :label="t('Flow')" value="flow"></ui-radio>
                <ui-radio :label="t('Built-in flow')" value="flowInline"></ui-radio>
                <ui-radio :label="t('URL')" value="url"></ui-radio>
              </ui-radio-group>

              <ui-radio-group
                v-model="form.redirectMode"
                class="w50"
                v-if="form.destinationType !== 'flow'"
                :label="t('Redirect mode')"
                :labelHelp="t('302 -  the fastest redirect mode. The referrer data will not be hidden. <br><br>Meta will hide traffic source data from affiliate network. Referrer will be sent as Click URL if landing page used or Campaign URL.<br><br>Double Meta will hide your landing page and traffic source data from affiliate network. A referrer will be equal to the cloaking domain you select.')"
              >
                <ui-radio value="302" :label="t('302')" />
                <ui-radio value="meta" :label="t('Meta')" />

                <div
                  :class="{
                    'plan-limits-warning__doubleMeta': showPlanLimitsWarning_doubleMeta,
                  }"
                >
                  <ui-tooltip-icon
                    class="plan-limits-warning__tooltip"
                    preset="error"
                    :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
                    v-if="showPlanLimitsWarning_doubleMeta"
                  ></ui-tooltip-icon>
                  <ui-radio
                    value="doubleMeta"
                    :label="t('Double meta')"
                    :plan-limits="'doubleMetaRedirect'"
                  />
                </div>
              </ui-radio-group>
            </div>

            <div v-if="form.destinationType === 'flow'">
              <ui-select
                v-model="form.flow"
                :placeholder="t('Select flow for your campaign…')"
                :label="t('Flow') + '*'"
                :items="flowOptions"
                item-text="nameFull"
                item-value="id"
                :return-object="true"
                :hide-details="2"
                attach
                :fetchOptions="fetchFlowOptions"
                :error-messages="errorsDict['flowId']"
                :loading="!store.cache.flows.isUploaded"
              ></ui-select>

              <create-campaign-dialog-flow-preview
                v-if="form.destinationType === 'flow' && form.flow && form.flow.id"
                :updateFlow="updateFlow"
                :flow="form.flow"
              ></create-campaign-dialog-flow-preview>
            </div>

            <div class="d-flex traffic-dist-cloaking-wrp">
              <div
                v-if="form.destinationType === 'flowInline'"
                class="d-flex"
                :class="{
                  'plan-limits-warning__trafficDistribution': showPlanLimitsWarning_rotation,
                  'pr-16': form.redirectMode === 'doubleMeta',
                }"
              >
                <ui-tooltip-icon
                  class="plan-limits-warning__tooltip"
                  preset="error"
                  :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
                  v-if="showPlanLimitsWarning_rotation"
                ></ui-tooltip-icon>
                <ui-select
                  v-model="form.rotation"
                  :items="rotationOptions"
                  :label="t('Traffic Distribution')"
                  item-value="key"
                  item-text="value"
                  :hide-details="2"
                  menuContentClass="create-campaign__select-smart-rotation-menu"
                />
              </div>

              <div class="d-flex" v-if="form.destinationType !== 'flow' && form.redirectMode === 'doubleMeta'">
                <ui-select
                  :value="form.redirectDomainId || ''"
                  @change="form.redirectDomainId = $event"
                  :placeholder="t('Cloaking domain')"
                  :label="t('Cloaking domain')"
                  :label-help="t('This domain will be applied to hide the referrer data')"
                  :items="store.redirectDomainsWithAccountDefault"
                  item-text="domain"
                  item-value="id"
                  :return-object="false"
                  :hide-details="2"
                  dialog-reposition-param="campaigns-dialog"
                ></ui-select>
              </div>
            </div>

            <div class="convert-to-flow">
              <ui-button
                class="mb-4 convert-to-flow"
                :disabled="convertToFlowDisabled"
                v-if="form.destinationType === 'flowInline'"
                size="mini"
                icon="fas fa-sitemap"
                :text="t('Convert to flow')"
                @click="openCreateFlowDialog"
                :helpTooltip="!!convertToFlowDisabled && t('This action is unavailable.<br>To transfer conditions and elements from the built-in flow and save them in a flow, you need to change the traffic distribution type to random.<br>Additionally, the built-in flow must contain at least one offer.')"
              />
            </div>

            <div v-bind="getIntroParams(5)">
              <div v-if="form.destinationType === 'url'">
                <ui-create-url-tags
                  :chips="chips"
                  :label="t('Destination URL') + '*'"
                  v-model="destinationUrl"
                  :has-errors="!!errorsDict['destinationUrl']"
                ></ui-create-url-tags>
              </div>
            </div>
            <template v-if="flowInlineRenderedOnce">
              <div v-show="form.destinationType === 'flowInline'">
                <create-campaign-dialog-flow-rule
                  v-for="(rule, ruleIndex) in form.flowInline.rules"
                  :isDisabledWeight="isDisabledWeight"
                  :key="rule"
                  :country-id="form.countryId"
                  :rule="rule"
                  :rules="form.flowInline.rules"
                  :form="form"
                  :rule-index="ruleIndex"
                  :errors-raw="errorsRaw"
                  :error-condition-prefix="['flowInline']"
                  dialog-reposition-param="campaigns-dialog"
                  @add-new-rule="addNewRule(rule)"
                  :offerOptions="offerOptions"
                  :landingOptions="landingOptions"
                ></create-campaign-dialog-flow-rule>
                <ui-button
                  @click="addNewRule()"
                  size="large"
                  button-style="plain"
                  :text="t('+ Add new rule')"
                  class="ml-0 mt-12 wide-btn"
                ></ui-button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <section
        class="justify-space-between align-center xs12"
        style="display: flex; width: 100%"
        v-if="step === 1"
      >
        <div>
          <error-aggregator
            v-model="errors"
            v-model:active="errorsControlActive"
          ></error-aggregator>
        </div>

        <div class="d-flex justify-end align-center">
          <ui-update-info v-if="mode === 'edit'" :data="form"></ui-update-info>
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            @click="checkClose"
          ></ui-button>

          <ui-button
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            :disabled="isActionsDisabled"
            class="d-flex mt-0 mb-0 ml-8 ml-first-0"
            @click="save"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import CreateCampaignDialogPanelOne from './components/create-campaign-dialog-panel-one.vue';
import CreateCampaignDialogFlowPreview from './components/create-campaign-dialog-flow-preview.vue';
import CreateCampaignDialogFlowRule from './components/create-campaign-dialog-flow-rule.vue';
import { computed } from 'vue';
import {
  useCampaignDialog,
  type CampaignDialogData,
} from './useCampaignDialog';

const props = withDefaults(
  defineProps<{
    data?: CampaignDialogData;
  }>(),
  {
    data: () => ({}),
  },
);

const emit = defineEmits<{
  close: [];
}>();

const mode = computed(() => props.data.mode ?? 'create');

const {
  store,

  t,
  show,
  step,
  form,
  edit,
  chips,
  flowOptions,
  offerOptions,
  landingOptions,
  errorsRaw,
  errors,
  errorsControlActive,
  errorsDict,
  campaignInitialState,
  scrollPanelDestinationRef,
  workspacesList,
  defaultWorkspace,
  rotationOptions,
  modalTitle,
  destinationUrl,
  isActionsDisabled,
  convertToFlowDisabled,
  isDisabledWeight,
  allWorkspaceIds,
  showPlanLimitsWarning_rotation,
  showPlanLimitsWarning_doubleMeta,
  fetchFlowOptions,
  flowInlineRenderedOnce,
  defaultForm,
  getIntroParams,
  checkClose,
  close,
  changeTrackingDomain,
  onChangeWorkspace,
  onChangeCountry,
  destinationTypeChange,
  openCreateFlowDialog,
  updateFlow,
  addNewRule,
  save,
} = useCampaignDialog(mode.value, () => props.data, () => emit('close'));
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.wide-btn {
  width: 100%;
  display: flex;

  :deep(.ui-button) {
    width: 100%;
    justify-content: center;
  }

  :deep(.ui-button__main) {
    justify-content: center;
  }
}

.help-circle {
  pointer-events: auto;
}

.update-info {
  display: flex;

  :deep(.name) {
    max-width: 250px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}

@media (max-width: 900px) {
  .update-info {
    display: none;
  }
}

.plan-limits-warning__trafficDistribution,
.plan-limits-warning__doubleMeta {
  position: relative;
  &:before {
    content: '';
    display: block;
    border: 1px solid var(--color-red);
    left: -8px;
    bottom: -3px;
    top: -4px;
    right: 0;
    position: absolute;
  }
}

.plan-limits-warning__trafficDistribution {
  &:before {
    bottom: 4px;
    right: -8px;
  }
  .plan-limits-warning__tooltip {
    right: 0;
  }
}

.plan-limits-warning__doubleMeta {
  .plan-limits-warning__tooltip {
    position: absolute;
    right: 6px;
    top: -1px;
    z-index: 10;
  }
}

.traffic-dist-cloaking-wrp {
  & > div {
    width: 50%;
    min-width: 0;

    &:only-child {
      width: 100%;
    }
  }

  :deep(> div > div:has(.ui-select__wrapper)) {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
  }

  :deep(.ui-select__wrapper) {
    width: 100%;
  }

  :deep(.ui-select.v-text-field),
  :deep(.v-select) {
    width: 100%;
  }
}
</style>

<style lang="scss">
@import '@/styles/dialogs/campaign-dialog';

.campaign-details {
  &__treeview-header {
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__condition {
    color: var(--color-green);
    & + & {
      &::before {
        content: '|';
        color: var(--color-black);
        margin: 0 3px;
      }
    }

    &--opposite-rule {
      color: var(--color-red);
    }

    &--elipsis {
      color: var(--color-black);
    }
  }
}

.v-dialog__content.v-dialog__content--active
  .v-dialog.v-dialog--active
  .v-input
  .v-input__slot {
  .v-menu {
    position: absolute;
    bottom: 0;
  }

  .v-menu__content {
    left: 0 !important;
    top: 0 !important;

    &:not(.v-autocomplete__content) {
      .v-card.v-select-list {
        padding: 0;
      }
    }

    &.v-autocomplete__content {
      .v-list {
        padding: 0 !important;
      }

      .v-card.v-select-list {
        padding: 6px 0;

        input {
          position: absolute;
        }
      }
    }
  }
}

.rule-campaigns {
  margin-top: 6px;

  & + & {
    margin-top: 24px;
  }

  &:first-child {
    .rule-campaigns__header-title {
      text-decoration: unset;
      color: unset;
    }
  }

  &__delete-button {
    &:hover {
      color: var(--color-red) !important;
      cursor: pointer;
    }
  }

  &__edit-button {
    &:hover {
      color: var(--color-green) !important;
      cursor: pointer;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__header-title {
    display: inline-flex;
    justify-content: flex-start;
    flex-direction: row;
  }

  &__header-name {
    margin-right: 10px;
    min-width: 0;

    .ui-input__wrapper.path-name {
      display: flex;
      flex-direction: column;
      width: 180px;
      max-width: 100%;
      min-width: 0;

      .ui-input,
      .v-text-field {
        width: 100%;
        min-width: 0;
      }
    }
  }

  &__header-actions {
    display: flex;
    align-items: center;
  }

  &__header-action-block {
    text-align: center;
    display: flex;
    padding: 0 10px;

    &.disabled {
      pointer-events: none;
      color: var(--color-gray40) !important;
    }

    & .v-input--selection-controls {
      margin-top: 0;
    }
  }

  &__header-switch {
    padding-right: 2px !important;
  }

  &__header-action-block + &__header-action-block {
    border-left: 1px solid var(--color-gray20);
  }

  &__header-action-move {
    display: flex;
    align-items: center;
  }

  &__header-action-move + &__header-action-move {
    margin-left: 5px;
  }

  &_inactive {
    .rule-campaigns__header {
      margin-bottom: 0;
      .rule-campaigns__header-title {
        text-decoration: line-through;
      }
    }
    .rule-campaigns__header-action-block {
      display: none;

      &_always-show {
        display: block;
        border-left: 0;
      }
    }

    .rule-campaigns__content,
    .rule-campaigns__footer {
      display: none !important;
    }
  }

  .rule-campaigns__header-title {
    color: var(--color-green);
    text-decoration: unset;
    flex-shrink: 1;
    flex-grow: 0;
    min-width: 0;
  }
  .rule-campaigns__header-conditions {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
  .rule-campaigns__header-title.class-equals {
    color: var(--color-red);
  }
}

.built-in-flow-item {
  width: 100%;
  height: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.offer-select-item {
  max-width: 100%;

  &__main-block {
    max-width: 100%;
    overflow: hidden;
    line-height: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__affiliate-network {
    font-size: 11px;
    line-height: 12px;
    white-space: nowrap;
  }
}

.create-campaign__select-smart-rotation-menu {
  .v-list__tile {
    & > div {
      display: flex;
      width: 100%;
    }
  }

  .plan-limits-icon {
    position: relative;
    top: 2px;
  }
}

.convert-to-flow {
  display: flex;
  justify-content: flex-end;
}
</style>
